/**
 * Modal bloqueante de consentimento dos Termos de Uso (ATR-563).
 *
 * Dois estados, decididos por `status.acceptedVersion`:
 *  - `null`  → primeiro acesso ("Termos de Uso do CRM" + faixa de metadados);
 *  - string  → re-aceite ("Estamos atualizando nossos termos de uso" + caixa
 *              "O que mudou nesta versão" + data do aceite anterior).
 *
 * NÃO É FECHÁVEL por design: sem botão de fechar, `Esc` e clique fora
 * neutralizados. A única saída é aceitar (segue no CRM) ou recusar (desconecta).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  Check,
  CheckSquare,
  ChevronDown,
  Clock,
  FileText,
  Loader2,
  Lock,
  Maximize2,
  Minimize2,
  RefreshCw,
  ShieldCheck,
  ShieldQuestion,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useBranding } from '@/features/branding';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { TermsStatus } from '@/types/auth.types';
import {
  TERMS_READING_MINUTES,
  TERMS_SECTIONS,
  TERMS_VERSION,
  getTermsChanges,
} from './terms-content';
import { useAcceptTerms, useDeclineTerms } from './useTermsConsent';

// Sem ids próprios de propósito: o Radix gera os seus e liga
// `aria-labelledby`/`aria-describedby` do conteúdo ao `AlertDialogTitle` e ao
// `AlertDialogDescription` automaticamente. Sobrescrever o `id` deles rompia
// essa ligação — o dialog ficava sem nome nem descrição para leitor de tela.

interface TermsConsentModalProps {
  /** Estado de consentimento vindo de `GET /auth/me`. */
  status: TermsStatus;
}

export function TermsConsentModal({ status }: TermsConsentModalProps) {
  const { branding } = useBranding();
  const termsRef = useRef<HTMLDivElement>(null);
  const accept = useAcceptTerms();
  const decline = useDeclineTerms();

  // Leitura ampliada: aumenta a área de texto (e alarga o diálogo). NÃO é uma
  // rota de dispensa — o modal continua não-fechável nos dois modos.
  const [expanded, setExpanded] = useState(false);
  // `true` só enquanto sobra conteúdo abaixo do que está visível. Governa o
  // fade do rodapé: mantê-lo aceso no fim do scroll esconderia a última linha.
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  const measureScroll = useCallback(() => {
    const el = termsRef.current;
    if (!el) return;
    // Folga de 4px: alguns navegadores arredondam `scrollTop` fracionário e
    // nunca chegam à igualdade exata no fim da rolagem.
    setHasMoreBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 4);
  }, []);

  // Listener registrado uma única vez: `measureScroll` é estável, então não há
  // motivo para desmontar/remontar o listener a cada toggle.
  useEffect(() => {
    window.addEventListener('resize', measureScroll);
    return () => window.removeEventListener('resize', measureScroll);
  }, [measureScroll]);

  // Re-mede quando a altura visível muda: ao montar e ao alternar o modo de
  // leitura (a área cresce/encolhe, e o fade precisa reavaliar).
  useEffect(() => {
    measureScroll();
  }, [measureScroll, expanded]);

  const isUpdate = status.acceptedVersion !== null;
  const changes = isUpdate ? getTermsChanges(TERMS_VERSION) : undefined;
  const busy = accept.isPending || decline.isPending;
  const productName = branding.organizationName;

  // A versão enviada é a do texto EXIBIDO (o espelho do frontend), não a que o
  // servidor reportou: é assim que uma aba com bundle antigo é barrada com
  // `VALIDATION_TERMS_VERSION_MISMATCH` em vez de registrar aceite de um texto
  // que o usuário não viu.
  const handleAccept = () => accept.mutate(TERMS_VERSION);
  const handleDecline = () => decline.mutate(TERMS_VERSION);

  return (
    <AlertDialog open>
      <AlertDialogContent
        className={cn(
          'block max-h-[92vh] w-[calc(100%-2rem)] gap-0 overflow-y-auto rounded-2xl p-0 transition-[max-width] duration-300',
          expanded ? 'max-w-[880px]' : 'max-w-[640px]',
        )}
        // Não fechável. `Esc` é a ÚNICA rota de dispensa que sobra para
        // neutralizar aqui: o `AlertDialogContent` do Radix já fixa
        // `onPointerDownOutside`/`onInteractOutside` com `preventDefault()`
        // internamente — tanto que os OMITE do tipo de props (repassá-los seria
        // erro de compilação). Clique no overlay não fecha por construção.
        onEscapeKeyDown={(event) => event.preventDefault()}
        // Sem foco programático na área de texto: focá-la ao abrir acendia o
        // anel de foco, que parecia um campo editável em destaque. A região
        // segue com `tabIndex={0}` — quem navega por teclado chega nela pelo
        // Tab e aí o anel aparece, que é quando ele deve mesmo aparecer.
      >
        <div className="flex items-start gap-4 px-7 pt-6">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-primary to-primary/70 text-primary-foreground shadow-lg shadow-primary/30">
            {isUpdate ? (
              <RefreshCw className="h-[22px] w-[22px]" aria-hidden="true" />
            ) : (
              <ShieldCheck className="h-[22px] w-[22px]" aria-hidden="true" />
            )}
          </span>

          <div className="min-w-0 flex-1">
            <span
              className={cn(
                'mb-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em]',
                isUpdate
                  ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
                  : 'bg-primary/10 text-primary',
              )}
            >
              <span className="h-1 w-1 rounded-full bg-current" aria-hidden="true" />
              {isUpdate ? 'Termos atualizados' : 'Primeiro acesso'}
            </span>

            <AlertDialogTitle className="mb-1 text-xl font-extrabold tracking-tight">
              {isUpdate
                ? 'Estamos atualizando nossos termos de uso'
                : 'Termos de Uso do CRM'}
            </AlertDialogTitle>

            <AlertDialogDescription className="max-w-[52ch] text-[13.5px] leading-relaxed">
              {isUpdate
                ? `Alguns pontos mudaram desde o seu último aceite. Revise as alterações e confirme para continuar usando ${productName}.`
                : `Para começar a usar ${productName}, leia e aceite os termos abaixo. Registraremos a versão aceita e a data do seu consentimento.`}
            </AlertDialogDescription>

            {isUpdate && status.acceptedVersion && (
              <span className="mt-2 inline-flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                <Check className="h-3.5 w-3.5 text-success" aria-hidden="true" />
                Você aceitou a versão
                <VersionTag version={status.acceptedVersion} />
                {status.acceptedAt ? `em ${formatDate(status.acceptedAt)}` : null}
              </span>
            )}
          </div>

          <span className="hidden shrink-0 items-center gap-1.5 rounded-full border border-border bg-muted/70 px-2.5 py-1 text-[11px] font-semibold text-muted-foreground sm:inline-flex">
            <Lock className="h-3 w-3" aria-hidden="true" />
            Aceite obrigatório
          </span>
        </div>

        {isUpdate ? (
          changes && changes.length > 0 ? (
            <section className="mx-7 mt-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
              <h3 className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-amber-700 dark:text-amber-400">
                <ShieldQuestion className="h-3.5 w-3.5" aria-hidden="true" />
                O que mudou nesta versão
              </h3>
              <ul className="flex flex-col gap-1.5 text-[12.8px] leading-relaxed text-amber-800 dark:text-amber-300">
                {changes.map((change) => (
                  <li
                    key={change}
                    className="relative pl-4 before:absolute before:left-0 before:top-[7px] before:h-1.5 before:w-1.5 before:rounded-full before:bg-current"
                  >
                    {change}
                  </li>
                ))}
              </ul>
            </section>
          ) : null
        ) : (
          <div className="mx-7 mt-4 flex flex-wrap items-center gap-x-3.5 gap-y-2 rounded-xl border border-border bg-muted/50 px-3.5 py-2.5 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              Versão
              <VersionTag version={TERMS_VERSION} />
            </span>
            <span className="hidden h-3.5 w-px bg-border sm:block" aria-hidden="true" />
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              Leitura de {TERMS_READING_MINUTES} min
            </span>
            <span className="hidden h-3.5 w-px bg-border sm:block" aria-hidden="true" />
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <CheckSquare className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              Registro com data, hora e versão
            </span>
          </div>
        )}

        {/* Alternador de leitura. Fica ACIMA da caixa de texto e com rótulo
            escrito de propósito: um ícone solto no canto superior do diálogo
            seria lido como "fechar", e este modal não fecha. */}
        <div className="mx-7 mt-4 flex justify-end">
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            aria-pressed={expanded}
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11.5px] font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            {expanded ? (
              <>
                <Minimize2 className="h-3.5 w-3.5" />
                Reduzir leitura
              </>
            ) : (
              <>
                <Maximize2 className="h-3.5 w-3.5" />
                Ampliar leitura
              </>
            )}
          </button>
        </div>

        <div className="relative mx-7 mt-1.5 rounded-2xl border border-border bg-card">
          <div
            ref={termsRef}
            tabIndex={0}
            role="region"
            aria-label="Texto dos Termos de Uso"
            onScroll={measureScroll}
            className={cn(
              // `pb-14` reserva a altura exata do gradiente do rodapé: sem
              // isso o texto rola por baixo dele e a última linha fica cortada.
              'overflow-y-auto px-5 pb-14 pt-5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              expanded
                ? 'max-h-[62vh]'
                : isUpdate
                  ? 'max-h-[168px]'
                  : 'max-h-[262px]',
            )}
          >
            {TERMS_SECTIONS.map((section) => (
              <section key={section.number} className="mb-5 last:mb-8">
                <div className="mb-1.5 flex items-baseline gap-2">
                  <span className="font-mono text-[11px] font-semibold text-primary">
                    §{section.number}
                  </span>
                  <h3 className="text-[13.5px] font-bold tracking-tight">
                    {section.title}
                  </h3>
                </div>
                {section.body.map((block, index) =>
                  block.kind === 'p' ? (
                    <p
                      key={index}
                      className="mb-2 text-[12.8px] leading-relaxed text-muted-foreground last:mb-0"
                    >
                      {block.text}
                    </p>
                  ) : (
                    <ul
                      key={index}
                      className="mb-2 ml-4 list-disc space-y-1 text-[12.8px] leading-relaxed text-muted-foreground marker:text-primary/50 last:mb-0"
                    >
                      {block.items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  ),
                )}
              </section>
            ))}
          </div>

          {/* Só enquanto sobra texto abaixo. No fim da rolagem não há o que
              esmaecer — o gradiente cobriria justamente a última linha. */}
          {hasMoreBelow && (
            <div
              // Rampa longa (~40px): com o gradiente curto anterior a linha
              // seguinte chegava opaca até o corte e parecia vazar por baixo do
              // aviso. Agora ela dissolve, que é o recado pretendido.
              className="pointer-events-none absolute inset-x-px bottom-px flex h-[76px] items-end justify-center rounded-b-2xl bg-gradient-to-t from-card from-[46%] to-transparent pb-2"
              aria-hidden="true"
            >
              {/* Fundo sólido próprio: nenhum texto atravessa as letras do
                  aviso, qualquer que seja a posição da rolagem. */}
              <span className="inline-flex items-center gap-1.5 rounded-full bg-card px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                Role para continuar
                <ChevronDown className="h-3 w-3" />
              </span>
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-col gap-3 border-t border-border px-7 py-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-[34ch] text-[11.5px] leading-relaxed text-muted-foreground">
            {isUpdate ? (
              <>
                Ao recusar, você será{' '}
                <strong className="font-semibold text-foreground">desconectado</strong> e o
                acesso ficará suspenso até aceitar a nova versão.
              </>
            ) : (
              <>
                Ao recusar, você será{' '}
                <strong className="font-semibold text-foreground">desconectado</strong> com
                segurança e poderá aceitar em um próximo acesso.
              </>
            )}
          </p>

          <div className="flex shrink-0 items-center justify-end gap-2.5">
            <Button
              variant="outline"
              className="h-11 rounded-full px-5 text-[13.5px]"
              disabled={busy}
              onClick={handleDecline}
            >
              {decline.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
              Recusar e sair
            </Button>
            <Button
              className="h-11 rounded-full pl-5 pr-2 text-[13.5px]"
              disabled={busy}
              onClick={handleAccept}
            >
              {isUpdate ? 'Aceitar nova versão' : 'Aceitar e continuar'}
              <span
                className="grid h-7 w-7 place-items-center rounded-full bg-primary-foreground/20"
                aria-hidden="true"
              >
                {accept.isPending ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <ArrowRight />
                )}
              </span>
            </Button>
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function VersionTag({ version }: { version: string }) {
  return (
    <span className="rounded-md border border-border bg-card px-2 py-0.5 font-mono text-[11.5px] font-semibold text-foreground">
      {version}
    </span>
  );
}
